import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import {
  AgentProfileDto,
  AgentUserPublicDto,
  AppointmentDto,
  PropertyApplicationDto,
  PropertyApplicationStatus,
  PropertyDto,
} from '@workspace/data-types';
import { AlertSnackBarService } from '@workspace/ng-aura';
import {
  DiscussionProfileDialogComponent,
  DiscussionProfileDialogData,
} from '../../../discussion/components/discussion-profile-dialog/discussion-profile-dialog.component';
import { PropertyApplicationEngagementDisplayComponent } from '../engagement-display/property-application-engagement-display.component';
import { PropertyApplicationTimelineComponent } from '../timeline/property-application-timeline.component';

@Component({
  selector: 'etxe-property-application-owner-drawer',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    PropertyApplicationEngagementDisplayComponent,
    PropertyApplicationTimelineComponent,
  ],
  templateUrl: './property-application-owner-drawer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EtxePropertyApplicationOwnerDrawerComponent {
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly alertSnackbar = inject(AlertSnackBarService);

  readonly application = input<PropertyApplicationDto | null>(null);
  readonly property = input<PropertyDto | null>(null);
  readonly isOpen = input<boolean>(false);

  readonly closed = output<void>();
  readonly acceptRequested = output<PropertyApplicationDto>();
  readonly rejectRequested = output<PropertyApplicationDto>();

  readonly isProcessing = signal<boolean>(false);

  readonly agentUser = computed<AgentUserPublicDto | null>(() => {
    const app = this.application();
    if (!app) return null;
    const agent = app.agent;
    if (typeof agent === 'object' && agent !== null && 'user' in agent) {
      return typeof agent.user === 'object' && agent.user !== null
        ? (agent.user as AgentUserPublicDto)
        : null;
    }
    return null;
  });

  readonly agentProfile = computed<AgentProfileDto | null>(() => {
    const app = this.application();
    if (!app) return null;
    return typeof app.agent === 'object' ? (app.agent as AgentProfileDto) : null;
  });

  readonly appointmentDto = computed<AppointmentDto | null>(() => {
    const appt = this.application()?.appointment;
    return typeof appt === 'object' && appt !== null ? (appt as unknown as AppointmentDto) : null;
  });

  readonly agentName = computed<string>(() => {
    const user = this.agentUser();
    const first = user?.identity?.firstName?.trim();
    const last = user?.identity?.lastName?.trim();
    if (first || last) {
      return `${first || ''} ${last || ''}`.trim();
    }
    const profile = this.agentProfile();
    if (profile?.activity?.network) {
      return `Agent ${profile.activity.network}`;
    }
    return 'Agent immobilier';
  });

  readonly agentAvatarUrl = computed<string | null>(() => {
    const avatar = this.agentUser()?.identity?.avatar;
    if (typeof avatar === 'string') return avatar;
    return avatar?.url ?? null;
  });

  readonly agentInitials = computed<string>(() => {
    const user = this.agentUser();
    const first = user?.identity?.firstName?.trim()?.[0] || '';
    const last = user?.identity?.lastName?.trim()?.[0] || '';
    if (first || last) {
      return (first + last).toUpperCase();
    }
    const name = this.agentName();
    return name.charAt(0).toUpperCase() || 'AG';
  });

  readonly agentNetwork = computed<string>(() => {
    return this.agentAffiliation().displayBadge;
  });

  readonly agentAffiliation = computed<{
    network?: string;
    agencyName?: string;
    typeLabel: string;
    isTitulaire: boolean;
    displayBadge: string;
    siren?: string;
  }>(() => {
    const profile = this.agentProfile();
    const network = profile?.activity?.network?.trim();
    const agencyName = profile?.cards?.activeCard?.companyName?.trim();
    const isTitulaire = profile?.type === 'titulaire';
    const typeLabel = isTitulaire ? 'Titulaire de carte T' : 'Mandataire indépendant';
    const siren = profile?.cards?.activeCard?.siren;

    const hasBoth = Boolean(network && agencyName && network.toLowerCase() !== agencyName.toLowerCase());

    let displayBadge: string;
    if (hasBoth) {
      displayBadge = `${network} • ${agencyName}`;
    } else if (network) {
      displayBadge = network;
    } else if (agencyName) {
      displayBadge = agencyName;
    } else {
      displayBadge = typeLabel;
    }

    return {
      network,
      agencyName,
      typeLabel,
      isTitulaire,
      displayBadge,
      siren,
    };
  });

  readonly agentCardNumber = computed<string | null>(() => {
    return this.agentProfile()?.cards?.activeCard?.number ?? null;
  });

  readonly isAccepted = computed<boolean>(() => {
    return this.application()?.status === PropertyApplicationStatus.ACCEPTED;
  });

  readonly isRejected = computed<boolean>(() => {
    return this.application()?.status === PropertyApplicationStatus.REJECTED;
  });

  readonly isExpired = computed<boolean>(() => {
    return this.application()?.status === PropertyApplicationStatus.EXPIRED;
  });

  readonly isCancelled = computed<boolean>(() => {
    return this.application()?.status === PropertyApplicationStatus.CANCELLED;
  });

  readonly isClosed = computed<boolean>(() => {
    return this.isAccepted() || this.isRejected() || this.isExpired() || this.isCancelled();
  });

  readonly isFinalOfferReady = computed<boolean>(() => {
    const s = this.application()?.status;
    return s === PropertyApplicationStatus.OFFER_SUBMITTED;
  });

  readonly canAccept = computed<boolean>(() => {
    const s = this.application()?.status;
    return (
      s === PropertyApplicationStatus.OFFER_SUBMITTED ||
      s === PropertyApplicationStatus.VISITED ||
      s === PropertyApplicationStatus.PENDING
    );
  });

  close(): void {
    this.closed.emit();
  }

  openDiscussion(): void {
    const app = this.application();
    if (!app?.discussion) {
      this.alertSnackbar.info({
        title: 'Discussion',
        message: 'La discussion avec cet agent sera initialisée dès votre premier message.',
      });
      return;
    }
    this.closed.emit();
    void this.router.navigate(['/messages', app.discussion]);
  }

  onAccept(): void {
    const app = this.application();
    if (!app) return;
    this.acceptRequested.emit(app);
  }

  onReject(): void {
    const app = this.application();
    if (!app) return;
    this.rejectRequested.emit(app);
  }

  openAgentProfileModal(): void {
    const app = this.application();
    if (!app) return;
    const profile = this.agentProfile();
    const agentId = profile?._id ? profile._id.toString() : typeof app.agent === 'string' ? app.agent : '';
    if (!agentId) return;

    this.dialog.open<DiscussionProfileDialogComponent, DiscussionProfileDialogData>(
      DiscussionProfileDialogComponent,
      {
        width: '100%',
        maxWidth: '760px',
        maxHeight: '90vh',
        autoFocus: false,
        data: {
          participantId: agentId,
          participantType: 'agent',
          participantName: this.agentName(),
          participantAvatarUrl: this.agentAvatarUrl(),
          propertyId: this.property()?._id?.toString(),
          agentProfile: profile ?? undefined,
        },
      },
    );
  }
}
